// TODO: build 2FA
// import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {memo, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Tagline} from "@khanacademy/wonder-blocks-typography";
import {useQuery} from "@khan/data-gql";
import {
    parseUrl,
    redirect,
    reload,
    toRelativeUrl,
    useLocation,
} from "@khan/urls";

import AccessibilitySettings from "./accessibility/accessibility-settings.tsx";
import {
    ACCOUNT_SETTINGS_QUERY,
    mapDataToResult,
} from "./account-settings-query.ts";
import {SectionLoading} from "./components/section/section-loading.tsx";
import LanguageSettings from "./language-settings.tsx";
import SuccessToast from "./success-toast.tsx";
import VideoSettings from "./video/video-settings.tsx";

interface Props {
    targetKaid: string;
}

const PersonalizationSettings = ({targetKaid}: Props) => {
    const location = useLocation();
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [accountSettingsResult] = useQuery(ACCOUNT_SETTINGS_QUERY, {
        variables: {targetKaid},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
    });

    const handleSuccessfulChange = () => {
        setShowSuccessToast(true);
        return reload();
    };

    const handleSuccessfulLanguageChange = (
        userKaLocale: string | "unspecified",
    ) => {
        setShowSuccessToast(true);

        const url = parseUrl(location.href);
        // A full-page reload is required to ensure the effect of the change
        // to the user selected language is immediately visible, by explicitly
        // specifying the `?lang=` URL param.
        url.searchParams.set(
            "lang",
            // NOTE: English is default if there's no user preferred selection.
            userKaLocale === "unspecified" ? "en" : userKaLocale,
        );

        redirect(toRelativeUrl(url.href));
    };

    if (accountSettingsResult.status === "loading") {
        return <SectionLoading numberOfSections={3} numberOfLoadingLines={5} />;
    }

    if (accountSettingsResult.status === "error") {
        return (
            <Tagline style={styles.text}>
                {t`Oops! Looks like we don’t have data available for this user.`}
            </Tagline>
        );
    }

    if (accountSettingsResult.status === "success") {
        const mappedData = mapDataToResult({
            data: accountSettingsResult.data,
            error: undefined,
            loading: false,
        });

        const {actor, target, requestInfo, sessionHash} = mappedData;

        if (!actor || !target || !requestInfo || !sessionHash) {
            return (
                <Tagline style={styles.text}>
                    {t`Oops! Looks like we don’t have data available for this user.`}
                </Tagline>
            );
        }
        const {
            hideVisual,
            prefersReducedMotion,
            noColorInVideos,
            showCaptions,
            playbackRate,
            muteVideos,
            soundOn,
        } = target.settings;

        return (
            <View style={{gap: sizing.size_200}}>
                <VideoSettings
                    kaid={target.id}
                    initialSettings={{
                        muteVideos,
                        showCaptions,
                        playbackRate: playbackRate || "RATE_100_PERCENT",
                    }}
                    successfullyChangedSettings={handleSuccessfulChange}
                />
                <LanguageSettings
                    initialUserKaLocale={
                        target.settings.userLanguage ?? "unspecified"
                    }
                    kaid={target.id}
                    successfullyChangedSettings={handleSuccessfulLanguageChange}
                />
                <AccessibilitySettings
                    initialSettings={{
                        hideVisual,
                        prefersReducedMotion,
                        noColorInVideos,
                        soundOn,
                    }}
                    kaid={target.id}
                    successfullyChangedSettings={handleSuccessfulChange}
                />
                {showSuccessToast && (
                    <SuccessToast onClose={() => setShowSuccessToast(false)} />
                )}
            </View>
        );
    }
};

const styles = StyleSheet.create({
    text: {
        color: semanticColor.core.foreground.neutral.strong,
    },
});

export default memo(PersonalizationSettings);
